import './styles/JobOfferDetail.scss';

import {
  Schedule,
  Category,
  GetApp,
  Add,
  HighlightOff,
  Launch,
  InfoOutlined
} from '@mui/icons-material';
import {
  Grid,
  Paper,
  Divider,
  Button,
  Chip,
  Fab,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  TextField,
  Box,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { attachmentDownload } from 'api';
import { isBefore } from 'date-fns';
import parse from 'html-react-parser';
import { orderBy } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { useJobPricesQuery } from 'api/hooks/jobmarket/jobprices';
import { useJobOfferingQuery, useJobOfferingsMutation } from 'api/hooks/jobmarket/offers';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { FormWrapperModal } from 'app/components/FormWrapperModal';
import { Modal } from 'app/components/Modal';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { WatchlistAddButton } from 'app/components/WatchlistAddButton';
import { Ordered } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { JobOffering } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import Roles from 'utils/enums/Roles';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import usePageHeading from 'utils/hooks/usePageHeading';

import { JobOfferDeactivationModal } from '../JobOfferDeactivationModal';
import { ManageOffer } from '../ManageOffer';
import { JobOfferDetailContact } from './components/JobOfferDetailContact';
import JobOfferPeriod from './components/JobOfferPeriod';

export const JobOfferDetail = () => {
  const { t } = useTranslation();
  const [createOfferModalOpen, setCreateOfferModalOpen] = useState(false);
  const [newPeriodModalOpen, setNewPeriodModalOpen] = useState(false);
  const [addingPeriod, setAddingPeriod] = useState(false);
  const [newStartDate, setNewStartDate] = useState<Date | null | undefined>(null);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user: currentUser } = useAuthContext();
  const aclHelper = new ACLHelper(currentUser);
  const { data: jobPriceData } = useJobPricesQuery();
  const [newPeriodPriceId, setNewPeriodPriceId] = useState<string>('');

  const { data: offer, isError, error, isLoading } = useJobOfferingQuery(parseInt(id!));

  const { extend } = useJobOfferingsMutation();

  usePageHeading({
    heading: t(translations.pages.jobOffers.detailTitle()),
    headingProps: {
      back: location.state?.back ? location.state.back : '/jobs'
    }
  });

  // TODO Überlegen ob das noch benötigt wird
  const studentView = /*!aclHelper.isCompanyUser() && */ !aclHelper.hasOne('entitlement', [
    Roles.JOBMARKET_ADMIN
  ]);

  const orderPeriods = (a, b) => {
    if (a.startDate > b.startDate) {
      return 1;
    } else if (a.startDate < b.startDate) {
      return -1;
    }
    return 0;
  };
  const handleAdding = async offer => {
    if (addingPeriod) {
      return;
    }
    setAddingPeriod(true);
    if (newPeriodPriceId) {
      await extend
        .mutateAsync({
          id: offer.id,
          data: {
            date: !!newStartDate ? newStartDate : undefined,
            jobPriceId: parseInt(newPeriodPriceId, 10)
          }
        })
        .then(() => {
          setNewStartDate(null);
          setNewPeriodModalOpen(false);
        })
        .catch(e => {
          dispatch(AlertActions.error(`${e.message}`));
        });
    }
    setAddingPeriod(false);
  };

  const getApplyToEmail = (offer: JobOffering) => {
    if (offer.creator) {
      return offer.creator.email;
    } else {
      return offer.company.mainContact.email;
    }
  };

  if (isError) {
    dispatch(
      AlertActions.error(`${t(translations.pages.jobOffers.openFailed())}: ${error.message}`)
    );
    navigate('/jobs', { replace: true });
  }
  if (isLoading || !offer) {
    return <CircularProgress />;
  }

  // ? This checks if there are any active jobperiods that are not yet over and are not cancelled
  const isDeactiveButtonDisabled = !offer.jobPeriods.find(
    jp => !jp.isCanceled && !isBefore(new Date(jp.endDate), new Date())
  );
  const userCanEdit =
    aclHelper.isJobmarketAdmin() ||
    (aclHelper.isEmployee() && offer.creator?.id === currentUser.id) ||
    (aclHelper.isCompanyUser(offer.company.id) && currentUser.company.state === 'active');

  if ((!offer.isActive || offer.company.state !== 'active') && !userCanEdit) {
    dispatch(AlertActions.error(`${t(translations.pages.jobOffers.notFound())}`));
    navigate('/jobs', { replace: true });
  }

  const pastPeriods = offer.jobPeriods.filter(p => new Date(p.endDate) < new Date());
  const periods = offer.jobPeriods.filter(p => new Date(p.endDate) > new Date());

  return (
    <>
      {userCanEdit && (
        <>
          <FormWrapperModal
            className="job-offer-detail-modal"
            open={createOfferModalOpen}
            onClose={(_event, reason) => {
              if (reason !== 'backdropClick') setCreateOfferModalOpen(false);
            }}
          >
            <div className="job-offer-detail-modal__body">
              <ManageOffer
                offerId={offer.id}
                company={offer.company}
                closeHandler={() => {
                  setCreateOfferModalOpen(false);
                }}
              />
            </div>
          </FormWrapperModal>
          <Modal
            open={deactivateModalOpen}
            onClose={() => setDeactivateModalOpen(false)}
            headline={t(translations.pages.deactivateJobOffers.title())}
            onCancel={() => setDeactivateModalOpen(false)}
          >
            <JobOfferDeactivationModal
              jobOffering={offer}
              closeHandler={() => setDeactivateModalOpen(false)}
            />
          </Modal>
        </>
      )}
      <Grid container className="job-offer-detail">
        <Grid item xs={12} className="job-offer-detail__section">
          {userCanEdit && (
            <Grid container justifyContent="flex-end" gap={2}>
              <Button
                variant="contained"
                sx={{ color: 'white' }}
                startIcon={<InfoOutlined />}
                onClick={() => setCreateOfferModalOpen(true)}
              >
                {t(translations.common.edit())}
              </Button>
              <Tooltip
                title={
                  isDeactiveButtonDisabled
                    ? t(translations.pages.deactivateJobOffers.tooltip())
                    : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<HighlightOff />}
                    onClick={() => setDeactivateModalOpen(true)}
                    disabled={isDeactiveButtonDisabled}
                  >
                    {t(translations.pages.deactivateJobOffers.button())}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          )}
        </Grid>
        <JobOfferDetailContact offer={offer} studentView={studentView} userCanEdit={userCanEdit} />
        <Grid component={Paper} square item xs={12} md={9} className="job-offer-detail__job">
          <Grid container>
            <Grid item xs={12} md={9} className="job-offer-detail__job__content">
              {offer.courses?.map(c => (
                <Chip
                  key={c.id}
                  className="joboffer__body__courses__course"
                  label={c.shorthand}
                  variant="outlined"
                  color="primary"
                  size="small"
                />
              ))}
              <Grid container className="job-offer-detail__job__content__title">
                {offer.title}
              </Grid>
              {aclHelper.isJobmarketAdmin() && (
                <Grid container className="job-offer-detail__job__content__id">
                  {`ID: ${offer.id}`}
                </Grid>
              )}
              <div className="job-offer-detail__job__content__description">
                <Box className="ql-editor">{parse(offer.description)}</Box>
              </div>
            </Grid>
            <Grid item xs={12} md={3}>
              {(userCanEdit || aclHelper.isStudent()) && (
                <Grid
                  container
                  className="job-offer-detail__job__start-date"
                  direction={'row'}
                  gap={2}
                >
                  <Button
                    startIcon={<Launch />}
                    variant="contained"
                    target="_blank"
                    href={
                      offer.externalLink ||
                      `mailto:${getApplyToEmail(offer)}?subject=Bewerbung ${offer.title}`
                    }
                    className="job-offer-detail__job__mailto"
                  >
                    {t(translations.pages.jobOffers.applyNow())}
                  </Button>
                  <WatchlistAddButton
                    categoryName="JOB_OFFER"
                    description={offer.description}
                    title={offer.title}
                    date={new Date()}
                  />
                </Grid>
              )}
              <Grid container className="job-offer-detail__job__start-date">
                <Schedule className="job-offer-detail__job__start-date__icon" />{' '}
                <div style={{ fontWeight: 'normal', whiteSpace: 'pre' }}>
                  {t(translations.pages.jobOffers.start()) + ':'}{' '}
                </div>
                {offer.startDate}
              </Grid>
              <Grid container className="job-offer-detail__job__category">
                <Grid item xs="auto">
                  <Category className="job-offer-detail__job__category__icon" />{' '}
                </Grid>
                <Grid item xs>
                  {offer.jobType.name}
                </Grid>
              </Grid>
              {offer.attachement && (
                <>
                  <Button
                    size="small"
                    className="job-offer-detail__job__download"
                    startIcon={<GetApp />}
                    onClick={() =>
                      handleDownload(offer.id.toString(), attachmentDownload(offer.id))
                    }
                  >
                    {t(translations.pages.jobOffers.downloadAttachement())}
                  </Button>
                </>
              )}
            </Grid>
          </Grid>
          {userCanEdit && (
            <>
              <Divider />
              <Grid container className="job-offer-detail__periods">
                <Grid container className="job-offer-detail__periods__header">
                  {`${t(translations.common.period())}`}
                </Grid>
                <Grid container spacing={2}>
                  {periods.length > 0 &&
                    periods.sort(orderPeriods).map(p => {
                      return (
                        <Grid item xs={6} sm={4} lg={3} xl={2} key={p.id}>
                          <JobOfferPeriod
                            key={p.id}
                            period={p}
                            canceled={p.isCanceled}
                            user={currentUser}
                            companyId={offer.company.id}
                            creatorId={offer.creator?.id}
                          />
                        </Grid>
                      );
                    })}
                  <Grid item xs="auto" className="job-offer-detail__periods__add">
                    <span
                      style={{
                        position: 'relative'
                      }}
                    >
                      <Fab
                        aria-label="add"
                        size="small"
                        disabled={addingPeriod}
                        style={{
                          backgroundColor: !addingPeriod ? '#f39100' : undefined
                        }}
                        onClick={() => setNewPeriodModalOpen(true)}
                      >
                        <Add />
                      </Fab>
                      {addingPeriod && (
                        <CircularProgress
                          size={28}
                          style={{
                            color: 'orange',
                            position: 'absolute',
                            left: 'calc(50% - 13px)',
                            top: 'calc(50% - 13px)'
                          }}
                        />
                      )}
                    </span>
                  </Grid>
                  <ConfirmationDialog
                    open={newPeriodModalOpen}
                    title={t(translations.pages.manageJobOffers.addPeriod.title())}
                    onClose={() => {
                      setNewPeriodModalOpen(false);
                    }}
                    onConfirm={() => handleAdding(offer)}
                    isPending={extend.isPending}
                  >
                    <Grid container>
                      <Grid container>
                        {t(translations.pages.manageJobOffers.addPeriod.messageNoInterval())}
                      </Grid>
                      {periods.length === 0 && (
                        <Grid container>
                          <DatePicker
                            className="job-offer-detail__periods__add__start-date"
                            disablePast
                            renderInput={props => (
                              <TextField
                                {...props}
                                variant="standard"
                                label={t(translations.pages.manageJobOffers.periodStart())}
                              />
                            )}
                            inputFormat="dd.MM.yyyy"
                            value={newStartDate}
                            onChange={e => setNewStartDate(e)}
                          />
                        </Grid>
                      )}
                      {
                        <RadioGroup
                          value={newPeriodPriceId}
                          onChange={e => setNewPeriodPriceId(e.target.value)}
                        >
                          {jobPriceData &&
                            jobPriceData
                              .filter(price =>
                                offer.jobType.paid ? price.price > 0 : price.price === 0
                              )
                              ?.map(price => {
                                return (
                                  <FormControlLabel
                                    key={price.id}
                                    value={price.id}
                                    control={<Radio />}
                                    label={`${price.name} (${t(
                                      translations.pages.manageJobOffers.factors[
                                        price.duration < 2 ? price.factor : `${price.factor}_plural`
                                      ](),
                                      { duration: price.duration }
                                    )}, ${price.price}€)`}
                                  />
                                );
                              })}
                        </RadioGroup>
                      }
                    </Grid>
                  </ConfirmationDialog>
                </Grid>
                {pastPeriods.length > 0 && (
                  <>
                    <Grid container className="job-offer-detail__periods__header">
                      {`${t(translations.common.pastPeriods())}`}
                    </Grid>
                    <Grid container spacing={2}>
                      {orderBy(pastPeriods, 'id', Ordered.Descending).map(p => {
                        return (
                          <Grid item xs={6} sm={4} lg={3} xl={2} key={p.id}>
                            <JobOfferPeriod
                              key={p.id}
                              period={p}
                              canceled={p.isCanceled}
                              user={currentUser}
                              companyId={offer.company.id}
                              creatorId={offer.creator?.id}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};
